import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, delay, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AppState } from '@api-ui-app/src/app/app.state';
import { ECommerceStoresApiService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-stores-api.service';
import { getSelectedStoreId } from '../../ngrx/e-commerce-stores.selector';
import { ImportProductsResponse } from '@gelato-api-ui/core/e-commerce/import-products-response';
import { getUserEmail } from '../../ngrx/auth.reducer';
import { EStoreSyncStatusResponse } from '@gelato-api-ui/core/e-commerce/e-store-sync-status-response';
import {
  isSyncStoreProductsActive,
  isSyncStoreProductsStatusActive,
} from '@gelato-api-ui/core/e-commerce/helpers/isSyncStoreProductsActive';
import { ShowGeneralErrorNotification } from '@gelato-api-ui/ui-kit/src/lib/notification/notification.actions';

@Injectable({ providedIn: 'root' })
export class ECommerceProductsSyncFacade {
  selectedStoreId$ = this.store.select(getSelectedStoreId);
  userEmail$ = this.store.select(getUserEmail);
  isSyncInProgress$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  syncProgressPercent$: BehaviorSubject<number> = new BehaviorSubject(0);
  isImportInProgress$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isExportInProgress$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private readonly store: Store<AppState>,
    private readonly eCommerceStoresApiService: ECommerceStoresApiService,
  ) {}

  resetState(): void {
    this.isSyncInProgress$.next(false);
    this.syncProgressPercent$.next(0);
    this.isImportInProgress$.next(false);
    this.isExportInProgress$.next(false);
  }

  importProducts(csvFileUrl: string, email: string): Observable<ImportProductsResponse> {
    this.isImportInProgress$.next(true);

    return this.selectedStoreId$.pipe(
      take(1),
      switchMap((selectedStoreId: string) =>
        this.eCommerceStoresApiService.importProducts(selectedStoreId, csvFileUrl, email, false).pipe(
          catchError(error => {
            this.isImportInProgress$.next(false);

            return throwError(error);
          }),
          tap((payload: ImportProductsResponse) => {
            this.isImportInProgress$.next(false);
          }),
        ),
      ),
    );
  }

  exportProducts(email: string): Observable<string> {
    this.isExportInProgress$.next(true);

    return this.selectedStoreId$.pipe(
      take(1),
      switchMap((selectedStoreId: string) =>
        this.syncProducts().pipe(
          switchMap(() =>
            this.eCommerceStoresApiService.exportProducts(selectedStoreId, email, false).pipe(
              catchError(error => {
                this.isExportInProgress$.next(false);

                return throwError(error);
              }),
              tap((payload: string) => {
                this.isExportInProgress$.next(false);
              }),
            ),
          ),
        ),
      ),
    );
  }

  syncProducts(): Observable<void> {
    this.isSyncInProgress$.next(true);
    this.syncProgressPercent$.next(0);

    return this.selectedStoreId$.pipe(
      take(1),
      switchMap((selectedStoreId: string) =>
        this.eCommerceStoresApiService.sync(selectedStoreId, false).pipe(
          catchError(() => {
            this.store.dispatch(new ShowGeneralErrorNotification());
            this.isSyncInProgress$.next(false);

            return of(null);
          }),
          delay(1000),
          switchMap(() =>
            this.eCommerceStoresApiService.getSyncStatusUntilFinished(selectedStoreId).pipe(
              filter((response: EStoreSyncStatusResponse): boolean => Boolean(response)),
              tap((response: EStoreSyncStatusResponse) => {
                this.updateSyncProgress(response);
              }),
              filter((response: EStoreSyncStatusResponse) => !isSyncStoreProductsActive(response)),
            ),
          ),
          map(() => undefined),
        ),
      ),
    );
  }

  private updateSyncProgress(response: EStoreSyncStatusResponse) {
    const isSyncInProgress = isSyncStoreProductsStatusActive(response.status);
    const isSyncInProgressPrev = this.isSyncInProgress$.getValue();

    if (!isSyncInProgress && isSyncInProgressPrev !== isSyncInProgress) {
      setTimeout(() => {
        this.isSyncInProgress$.next(isSyncInProgress);
      }, 1000);
    } else {
      this.isSyncInProgress$.next(isSyncInProgress);
    }

    this.syncProgressPercent$.next(response.percent || 0);
  }
}
